import { apiGet, apiPatch, apiPost } from '../../../adapters/api-client/api';
import { CourseDetailResponse, CourseSearchResult, SearchFilters } from './courses';
import { PublicTeacherProfile } from './teacher-profile';

/**
 * The child's space, UC-S02..S06. The server confines every call here to the
 * active learner and none of them takes a learner id: there is nothing to send
 * that could name a sibling (DEC-S2-S01-05).
 */

/** DEC-S2-S06-05: the one lifecycle, the same words Parent's screen reads. */
export type LearnerRequestState =
  | 'PENDING_PARENT'
  | 'PARENT_DECLINED'
  | 'PARENT_APPROVED_PENDING_TEACHER'
  | 'ENROLLED'
  | 'EXPIRED'
  | 'UNAVAILABLE';

export type LearnerRequestReason =
  | 'OFFER_WITHDRAWN'
  | 'OFFER_CHANGED'
  | 'SEAT_FULL'
  | 'SESSION_UNAVAILABLE'
  | 'LEARNER_OUT_OF_RANGE'
  | 'TEACHER_DECLINED'
  | 'ACCEPTANCE_EXPIRED'
  | 'PARENT_DECLINED'
  | 'PARENT_CANCELLED';

export interface LearnerRequestView {
  requestId: string;
  learnerId: string;
  offerId: string;
  courseId: string;
  courseVersionId: string;
  sessionInstanceId?: string;
  source: string;
  state: LearnerRequestState;
  reason?: LearnerRequestReason;
  courseTitle: string;
  teacherName?: string;
  scheduleType?: string;
  schedule?: CourseDetailResponse['schedule'];
  createdAt: string;
  updatedAt: string;
  version: number;
}

export type LearnerFit = 'FIT' | 'OUT_OF_RANGE' | 'UNKNOWN';
export type SeatAvailability = 'OPEN' | 'FULL' | 'UNKNOWN';
export type RequestBlock =
  | 'OFFER_UNAVAILABLE'
  | 'ALREADY_ENROLLED'
  | 'LEARNER_OUT_OF_RANGE'
  | 'SEAT_FULL'
  | 'AVAILABILITY_UNKNOWN';

export interface ExploreCourse extends CourseSearchResult {
  teacherName: string | null;
  fit: LearnerFit;
  availability: SeatAvailability;
  request: LearnerRequestView | null;
}

export interface ExploreResponse {
  items: ExploreCourse[];
  nextToken?: string;
  count: number;
}

/** `fit: 'all'` widens what is shown; it changes nothing about what can be requested. */
export interface ExploreQuery extends SearchFilters {
  fit?: 'mine' | 'all';
}

export function getExplore(query: ExploreQuery): Promise<ExploreResponse> {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  const text = params.toString();
  return apiGet(`/api/v1/me/learner/explore${text ? `?${text}` : ''}`);
}

export interface ExploreCourseDetail extends CourseDetailResponse {
  fit: LearnerFit;
  availability: SeatAvailability;
  eligibility: { requestable: boolean; reason?: RequestBlock };
  request: LearnerRequestView | null;
}

export function getExploreCourse(courseId: string): Promise<ExploreCourseDetail> {
  return apiGet(`/api/v1/me/learner/explore/${encodeURIComponent(courseId)}`);
}

export type StudentTeacherProfile = PublicTeacherProfile;

export function getLearnerTeacher(teacherId: string): Promise<StudentTeacherProfile> {
  return apiGet(`/api/v1/me/learner/teachers/${encodeURIComponent(teacherId)}`);
}

/** "Request This Class". A retry of the same intent answers with the same request. */
export function createLearnerRequest(input: {
  courseId: string;
  offerId: string;
}): Promise<LearnerRequestView> {
  return apiPost('/api/v1/me/learner/requests', input);
}

export function getLearnerRequests(): Promise<{ requests: LearnerRequestView[] }> {
  return apiGet('/api/v1/me/learner/requests');
}

export type InterestInput =
  | { reason: 'SEAT_FULL' | 'TIME_MISMATCH'; courseId: string; offerId: string }
  | { reason: 'NO_SUPPLY'; subject?: string; gradeLevel?: string };

/** DEC-S2-S06-07: demand for P06. Reserves nothing, charges nothing, joins no queue. */
export function registerInterest(input: InterestInput): Promise<{ created: boolean }> {
  return apiPost('/api/v1/me/learner/interest', input);
}

export interface LearnerProfile {
  studentId: string;
  firstName: string;
  grade: number;
  avatar: string | null;
  interests: string[];
  visualPreferences: { accent?: string };
  /** The server's closed lists, so the screen keeps no copy of them. */
  options: { avatars: string[]; interests: string[]; accents: string[] };
}

export function getLearnerProfile(): Promise<LearnerProfile> {
  return apiGet('/api/v1/me/learner/profile');
}

export function updateLearnerProfile(
  patch: Partial<Pick<LearnerProfile, 'avatar' | 'interests' | 'visualPreferences'>>,
): Promise<LearnerProfile> {
  return apiPatch('/api/v1/me/learner/profile', patch);
}
